import { Box, Flex, List, ListItem } from '@chakra-ui/react';
import { Notification, Paragraph, TextLink } from 'ui-components';
import { CONTACT_EMAIL } from '~/constants';

import { useSavedListStateStore } from '../SavedListsModal.store';
import {
  SavedListListError,
  SavedListNoteError,
  SavedListSubmitError,
} from '../SavedListsModal.types';

interface ErrorNotificationProps {
  errors: SavedListSubmitError[];
  setErrors: (errors: SavedListSubmitError[]) => void;
}

export const ErrorNotification: React.FC<ErrorNotificationProps> = ({
  errors,
  setErrors,
}) => {
  const { lists: state } = useSavedListStateStore();

  const hasListUpdateErrors = errors.some((error) => error.type === 'list');
  const hasNoteUpdateErrors = errors.some((error) => error.type === 'note');

  if (hasListUpdateErrors || hasNoteUpdateErrors) {
    const children: React.ReactNode[] = [];

    if (hasListUpdateErrors) {
      children.push(
        <Box
          flexBasis="100%"
          overflow="hidden"
          borderRadius="8"
          marginBottom={4}
          key="list-errors"
        >
          <Notification
            isFullWidth
            onClickClose={() =>
              setErrors([...errors].filter((error) => error.type !== 'list'))
            }
            title="Some Talents couldn’t be added to these lists:"
            variant="error"
          >
            <List marginBottom={4}>
              {errors
                .filter((error) => error.type === 'note')
                .map((error: SavedListListError) => (
                  <ListItem key={error.listId}>
                    {state[error.listId].name}.
                  </ListItem>
                ))}
            </List>

            <Paragraph>
              The rest of the selections were added successfully. Please reload
              and try again. If the issue persists,{' '}
              <TextLink
                variant="primary"
                href={`mailto:${CONTACT_EMAIL}`}
                sx={{ fontWeight: 'medium' }}
              >
                contact us
              </TextLink>
              .
            </Paragraph>
          </Notification>
        </Box>,
      );
    }

    if (hasNoteUpdateErrors) {
      children.push(
        <Box
          flexBasis="100%"
          overflow="hidden"
          borderRadius="8"
          marginBottom={4}
          key="note-errors"
        >
          <Notification
            isFullWidth
            onClickClose={() =>
              setErrors([...errors].filter((error) => error.type !== 'note'))
            }
            title="Some notes couldn’t be added to these lists:"
            variant="error"
          >
            <List marginBottom={4}>
              {errors
                .filter((error) => error.type === 'note')
                .map((error: SavedListNoteError) => {
                  const creator = state[error.listId].creators.find(
                    ({ id }) => id === error.creatorId,
                  );

                  return (
                    <ListItem key={error.creatorId}>
                      Failed to add{' '}
                      <Paragraph as="span" fontWeight="medium">
                        {creator?.name ?? 'Unknown'}
                      </Paragraph>{' '}
                      note to{' '}
                      <Paragraph as="span" fontWeight="medium">
                        {state[error.listId].name}
                      </Paragraph>
                      .
                    </ListItem>
                  );
                })}
            </List>

            <Paragraph>
              The rest of the selections were added successfully. Please reload
              and try again. If the issue persists,{' '}
              <TextLink
                variant="primary"
                href={`mailto:${CONTACT_EMAIL}`}
                sx={{ fontWeight: 'medium' }}
              >
                contact us
              </TextLink>
              .
            </Paragraph>
          </Notification>
        </Box>,
      );
    }

    return (
      <Flex flexDirection="column" gap={4}>
        {children}
      </Flex>
    );
  }

  if (errors.length) {
    return (
      <Box flexBasis="100%" overflow="hidden" borderRadius="8" marginBottom={4}>
        <Notification
          isFullWidth
          onClickClose={() => setErrors([])}
          title="We've encountered an error."
          variant="error"
        >
          Please reload and try again. If the issue persists,{' '}
          <TextLink
            variant="primary"
            href={`mailto:${CONTACT_EMAIL}`}
            sx={{ fontWeight: 'medium' }}
          >
            contact us
          </TextLink>
          .
        </Notification>
      </Box>
    );
  }

  return null;
};
